import React, { useEffect, useState } from 'react';
import { Card, Container, Row } from 'react-bootstrap';
import { isEmpty } from 'lodash';
import DynamicForm from 'screens/common/dynamicForm';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import FalconCardHeader from 'components/common/FalconCardHeader';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { pulaFarmlandApi } from 'api/endpoints/PulaFarmland';
import { formSubmissionToast } from 'helpers/utils';
import { getTenantFromToken } from 'utils/routeUtils';
import useOnlineStatus from 'hooks/useOnlineStatus';

/**
 * PulaFarmlandForm - Unified screen for creating and editing Pula Farmland records.
 * It uses DynamicForm to render the JSON schema provided by the backend.
 */
const PulaFarmlandForm = () => {
  const { id } = useParams(); // farmer_id (create) or pula_farm_id (edit)
  const location = useLocation();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const tenant = getTenantFromToken();

  const [formConfig, setFormConfig] = useState({});
  const [loading, setLoading] = useState(true);

  // Determine mode from URL path
  const isEdit = location.pathname.includes('/edit/');

  /**
   * Fetches the dynamic form configuration from the Pula API.
   */
  const fetchFormConfig = async () => {
    setLoading(true);
    try {
      if (isOnline) {
        const pulaFarmId = isEdit ? id : null;
        const farmerId = !isEdit ? id : null;

        const response = await pulaFarmlandApi.getForm(
          isEdit,
          pulaFarmId,
          farmerId
        );

        // Backend returns { results: { fields: [...], form: {...} } }
        if (response && response.results) {
          setFormConfig(response.results);
        } else {
          toast.error('Invalid form configuration received');
        }
      } else {
        toast.error('Farmland is currently only available online');
        navigate(-1);
      }
    } catch (error) {
      console.error('Error fetching pula form:', error);
      toast.error('Failed to load Farmland form');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormConfig();
  }, [id, isEdit, isOnline]);

  /**
   * Handles the submission of the dynamic form.
   */
  const handleFormSubmit = async formData => {
    try {
      let response;
      if (isEdit) {
        response = await pulaFarmlandApi.updateFarmland(id, formData);
      } else {
        response = await pulaFarmlandApi.createFarmland(formData);
      }

      formSubmissionToast(response, toast, 'Farmland');

      if (response && (response.success || response.status < 300)) {
        // Redirect back to the Pula Farmland report
        navigate(`/${tenant}/pulaFarmlandReport/`);
      }
    } catch (error) {
      console.error('Submission error:', error);
      formSubmissionToast(error, toast, 'Farmland');
    }
  };

  const headerTitle = isEdit ? 'Edit Farmland' : 'Create Farmland';

  const header = (
    <div className="d-flex align-items-center">
      <div
        className="me-2 my-auto theme-control-toggle"
        onClick={() => navigate(-1)}
        style={{ cursor: 'pointer' }}
      >
        <div className="theme-control-toggle-label">
          <FontAwesomeIcon icon={faArrowLeft} className="fs-0" />
        </div>
      </div>
      <span>{headerTitle}</span>
    </div>
  );

  return (
    <Container>
      <Row>
        <Card className="mb-3">
          <Card.Header>
            <FalconCardHeader
              title={header}
              className="ps-lg-4"
              titleTag="h5"
              titleClass="mb-0"
              light
            />
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center p-5">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading form configuration...</p>
              </div>
            ) : !isEmpty(formConfig) ? (
              <DynamicForm
                data={formConfig}
                header={true}
                onSubmit={handleFormSubmit}
                mode={isEdit ? 'edit' : 'create'}
              />
            ) : (
              <div className="text-center p-5">
                <p>No form configuration available.</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </Row>
    </Container>
  );
};

export default PulaFarmlandForm;
